import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AccessControlPanel = () => {
  const [activeTab, setActiveTab] = useState('users');

  const users = [
    {
      id: 'USR-001',
      name: 'Sarah Johnson',
      email: 'sarah.j@example.com',
      role: 'Super Admin',
      lastLogin: '2 mins ago',
      status: 'active',
      permissions: ['full_access', 'user_management', 'security_config']
    },
    {
      id: 'USR-002',
      name: 'Michael Chen',
      email: 'michael.c@example.com',
      role: 'Compliance Officer',
      lastLogin: '15 mins ago',
      status: 'active',
      permissions: ['compliance_review', 'reports', 'audit_logs']
    },
    {
      id: 'USR-003',
      name: 'David Wilson',
      email: 'david.w@example.com',
      role: 'Operations Manager',
      lastLogin: '1 hour ago',
      status: 'active',
      permissions: ['transaction_review', 'agent_management']
    },
    {
      id: 'USR-004',
      name: 'Emma Davis',
      email: 'emma.d@example.com',
      role: 'Auditor',
      lastLogin: 'Yesterday',
      status: 'inactive',
      permissions: ['read_only', 'audit_logs']
    }
  ];

  const securityEvents = [
    {
      id: 'SEC-001',
      type: 'failed_login',
      severity: 'high',
      description: 'Multiple failed login attempts from IP 192.168.1.105',
      timestamp: '5 mins ago',
      action: 'IP Blocked',
      user: 'Unknown'
    },
    {
      id: 'SEC-002',
      type: 'unusual_location',
      severity: 'medium',
      description: 'Login from new location: Tokyo, Japan',
      timestamp: '23 mins ago',
      action: 'MFA Required',
      user: 'michael.c@example.com'
    },
    {
      id: 'SEC-003',
      type: 'permission_change',
      severity: 'low',
      description: 'User role updated from Agent to Operations Manager',
      timestamp: '1 hour ago',
      action: 'Approved',
      user: 'david.w@example.com'
    },
    {
      id: 'SEC-004',
      type: 'api_rate_limit',
      severity: 'medium',
      description: 'API rate limit exceeded for client API-KEY-789',
      timestamp: '2 hours ago',
      action: 'Throttled',
      user: 'Integration Service'
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-error/10 text-error';
      case 'medium': return 'bg-warning/10 text-warning';
      case 'low': return 'bg-accent/10 text-accent';
      default: return 'bg-muted/50 text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'text-success' : 'text-muted-foreground';
  };

  const tabs = [
    { id: 'users', label: 'User Access', icon: 'Users', count: users?.length },
    { id: 'security', label: 'Security Events', icon: 'Shield', count: securityEvents?.length }
  ];

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Lock" size={20} />
          <span>Role-Based Access Control</span>
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          User permissions and security monitoring
        </p>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-colors relative
              ${activeTab === tab?.id
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
            <span className={`inline-flex items-center justify-center w-5 h-5 text-xs rounded-full
              ${activeTab === tab?.id ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
              {tab?.count}
            </span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'users' && (
          <div className="space-y-3">
            {users?.map((user) => (
              <div key={user?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{user?.name}</h4>
                      <p className="text-sm text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(user?.status)}
                      ${user?.status === 'active' ? 'bg-success/10' : 'bg-muted/50'}`}>
                      {user?.status?.toUpperCase()}
                    </span>
                    <button className="p-2 hover:bg-muted/50 rounded-lg">
                      <Icon name="MoreVertical" size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <span className="text-muted-foreground">Role:</span>
                    <span className="font-medium text-foreground">{user?.role}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>{user?.lastLogin}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
                  {user?.permissions?.map((perm, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent/10 text-accent">
                      {perm?.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-3">
            {securityEvents?.map((event) => (
              <div key={event?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getSeverityColor(event?.severity)}`}>
                      <Icon name="AlertTriangle" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-foreground">{event?.description}</h4>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span className="flex items-center space-x-1">
                          <Icon name="User" size={14} />
                          <span>{event?.user}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="Clock" size={14} />
                          <span>{event?.timestamp}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getSeverityColor(event?.severity)}`}>
                    {event?.severity?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <span className="text-sm text-muted-foreground">Action Taken:</span>
                  <span className="text-sm font-medium text-foreground">{event?.action}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessControlPanel;